import { motion } from 'framer-motion';

import { RunStatus } from '../../../../../../../backend/src/types/types';
import { cn } from '../../../../lib/utils';
import { Icon } from '@/new/components/icon';
import {
  PipelineProcessingStageDescriptions,
  PipelineProcessingStages,
  ProcessingStatus,
} from '@/new/lib/pipeline-status';

const getPipelineStatusIcon = (pipelineStatus: ProcessingStatus, index: number) => {
  const currentStage = PipelineProcessingStages[index];
  const isAwaitingApproval = currentStage === RunStatus.AwaitingApproval;

  switch (true) {
    case PipelineProcessingStages.indexOf(pipelineStatus) > index:
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
          }}
          className="flex items-center justify-center"
        >
          <Icon className="lucide--circle-check text-teal-500" />
        </motion.div>
      );
    case PipelineProcessingStages.indexOf(pipelineStatus) === index:
      if (isAwaitingApproval) {
        return <Icon className="lucide--shield-alert text-amber-600" />;
      }
      return <Icon className="lucide--loader-circle animate-spin text-blue-500 duration-1000" />;
  }
};

interface ProcessingPipelineProps {
  casePipelineStatus: RunStatus;
  requiresAdminApproval?: boolean;
}

export const ProcessingPipeline = ({ casePipelineStatus, requiresAdminApproval = false }: ProcessingPipelineProps) => {
  const stages = PipelineProcessingStages.filter(
    (status) =>
      status !== RunStatus.InQueue &&
      (status !== RunStatus.AwaitingApproval || casePipelineStatus === RunStatus.AwaitingApproval),
  );

  return (
    <div className="py-6">
      <div className="relative">
        {/* Stage icons and labels */}
        <div className="relative flex items-start justify-between" style={{ zIndex: 1 }}>
          {stages.map((status: ProcessingStatus) => {
            const currentIndex = PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus);
            const stageIndex = PipelineProcessingStages.indexOf(status);
            const isActive = stageIndex === currentIndex;
            const isCompleted = stageIndex < currentIndex;
            const isAwaitingApproval = status === RunStatus.AwaitingApproval;

            // Override the approval stage title if it requires admin approval
            const stageTitle =
              isAwaitingApproval && requiresAdminApproval
                ? 'Admin Approval'
                : PipelineProcessingStageDescriptions[status].title;

            return (
              <div key={status} className="group relative flex flex-1 flex-col items-center">
                <div className="absolute top-3 right-0 left-0 h-px bg-gray-300 group-first:left-1/2 group-last:right-1/2"></div>

                <div
                  className={cn(
                    'z-10 flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-white',
                  )}
                >
                  {getPipelineStatusIcon(casePipelineStatus as ProcessingStatus, stageIndex)}
                </div>

                <div
                  className={`mt-3 rounded-lg border px-2 py-1 text-center text-xs ${
                    isActive && isAwaitingApproval
                      ? 'border-amber-200 bg-amber-50 text-amber-700'
                      : isActive
                        ? 'border-blue-200 bg-blue-100 text-blue-700'
                        : isCompleted
                          ? 'border-green-200 bg-green-100/60 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {stageTitle}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
